import React, {useEffect, useMemo, useRef, useState} from "react";
import {LucideCircleSlash, LucideExternalLink} from "lucide-react";
import {useSocket} from "../SocketContext";
import {useServerInfo} from "../ServerInfoContext";
import {useOverlay} from "../overlay/OverlayContext";
import {
    rpc_getAllPermissions,
    rpc_getGroupData,
    rpc_getPermissionGroups,
    rpc_getPlayerPermissions,
    rpc_setGroupPermission,
    rpc_setPlayerGroupMembership,
    rpc_setPlayerPermission
} from "../rpc";
import {PermissionGroupInfo, PermissionPlayer, PlayerPermissions} from "./interfaces";
import {usePlayerDirectory} from "./usePlayerDirectory";
import TabButton from "./TabButton";
import SearchInput from "./SearchInput";
import PermissionList from "./PermissionList";
import GroupToggleList from "./GroupToggleList";
import GroupMembersPanel from "./GroupMembersPanel";
import AddPlayersOverlay from "./AddPlayersOverlay";

type Selection =
    | { type: "player", player: PermissionPlayer }
    | { type: "group", group: PermissionGroupInfo }

export default function Permissions() {

    const socket = useSocket();
    const serverInfo = useServerInfo();
    const overlay = useOverlay();

    const [leftTab, setLeftTab] = useState<"player" | "group">("player");
    const [leftSearch, setLeftSearch] = useState("");
    const [selected, setSelected] = useState<Selection | null>(null);
    const [rightTab, setRightTab] = useState<"permissions" | "membership">("permissions");

    const [allPermissions, setAllPermissions] = useState<string[]>([]);
    const [groups, setGroups] = useState<PermissionGroupInfo[]>([]);
    const [loaded, setLoaded] = useState(false);
    // false once the initial fetch comes back null (the plugin isn't responding / installed).
    const [available, setAvailable] = useState(true);

    const [playerPerms, setPlayerPerms] = useState<PlayerPermissions | undefined>();
    const [groupPerms, setGroupPerms] = useState<string[] | undefined>();
    const [groupMembers, setGroupMembers] = useState<PermissionPlayer[] | undefined>();

    const [showAddPlayers, setShowAddPlayers] = useState(false);

    // Players come from the same sources as the Players page (live online list + paged offline list + search).
    const playerDirectory = usePlayerDirectory(leftTab === "player" ? leftSearch : "");
    const playerListRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // The permission/group data comes from the game server over RCON, so only fetch (and refetch)
        // once it is actually connected.
        if (!socket.isConnected || !serverInfo.serverConnected) {
            return;
        }
        let cancelled = false;
        Promise.all([
            rpc_getAllPermissions(socket),
            rpc_getPermissionGroups(socket)
        ]).then(([perms, grps]) => {
            if (cancelled) {
                return;
            }
            // null => the plugin didn't respond (not installed); the two share a connection, so groups
            // is the canonical signal.
            setAvailable(grps != null);
            setAllPermissions(perms ?? []);
            setGroups(grps ?? []);
            setLoaded(true);
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socket.isConnected, serverInfo.serverConnected]);

    const selectPlayer = (p: PermissionPlayer) => {
        setSelected({type: "player", player: p});
        setRightTab("permissions");
        setPlayerPerms(undefined);
        rpc_getPlayerPermissions(socket, p.steamId).then(r => setPlayerPerms(r ?? {direct: [], groups: [], inherited: []}));
    };

    const selectGroup = (g: PermissionGroupInfo) => {
        setSelected({type: "group", group: g});
        setRightTab("permissions");
        setGroupPerms(undefined);
        setGroupMembers(undefined);
        rpc_getGroupData(socket, g.name).then(r => {
            setGroupPerms(r?.permissions ?? []);
            setGroupMembers(r?.members ?? []);
        });
    };

    const togglePermission = (permission: string, currentlyDirect: boolean) => {
        if (!selected) {
            return;
        }
        const granted = !currentlyDirect;
        const verb = granted ? "Grant" : "Revoke";
        const prep = granted ? "to" : "from";

        if (selected.type === "player") {
            const player = selected.player;
            overlay.showConfirmation(
                `${verb} permission?`,
                async () => {
                    const res = await rpc_setPlayerPermission(socket, player.steamId, permission, granted);
                    if (!res.success) {
                        return describeError(res.message);
                    }
                    setPlayerPerms(prev => prev ? {
                        ...prev,
                        direct: granted ? [...prev.direct, permission] : prev.direct.filter(p => p !== permission)
                    } : prev);
                    return undefined;
                },
                `${verb} "${permission}" ${prep} ${player.displayName}?`,
                verb
            );
        } else {
            const group = selected.group.name;
            overlay.showConfirmation(
                `${verb} permission?`,
                async () => {
                    const res = await rpc_setGroupPermission(socket, group, permission, granted);
                    if (!res.success) {
                        return describeError(res.message);
                    }
                    setGroupPerms(prev => prev ? (granted ? [...prev, permission] : prev.filter(p => p !== permission)) : prev);
                    return undefined;
                },
                `${verb} "${permission}" ${prep} group "${group}"?`,
                verb
            );
        }
    };

    const togglePlayerGroup = (group: string, isMember: boolean) => {
        if (!selected || selected.type !== "player") {
            return;
        }
        const member = !isMember;
        const player = selected.player;
        const verb = member ? "Add" : "Remove";
        const prep = member ? "to" : "from";
        overlay.showConfirmation(
            member ? "Add to group?" : "Remove from group?",
            async () => {
                const res = await rpc_setPlayerGroupMembership(socket, player.steamId, group, member);
                if (!res.success) {
                    return describeError(res.message);
                }
                // Refetch so inherited permissions reflect the new membership.
                const fresh = await rpc_getPlayerPermissions(socket, player.steamId);
                if (fresh) {
                    setPlayerPerms(fresh);
                }
                return undefined;
            },
            `${verb} ${player.displayName} ${prep} group "${group}"?`,
            verb
        );
    };

    const removeMember = (steamId: string) => {
        if (!selected || selected.type !== "group") {
            return;
        }
        const group = selected.group.name;
        const name = (groupMembers ?? []).find(m => m.steamId === steamId)?.displayName ?? steamId;
        overlay.showConfirmation(
            "Remove from group?",
            async () => {
                const res = await rpc_setPlayerGroupMembership(socket, steamId, group, false);
                if (!res.success) {
                    return describeError(res.message);
                }
                setGroupMembers(prev => prev?.filter(m => m.steamId !== steamId));
                return undefined;
            },
            `Remove ${name} from group "${group}"?`,
            "Remove"
        );
    };

    // No confirmation here: adding players is already a deliberate multi-step action (open the picker,
    // select players, click Add). The confirmations guard the single-click grant/revoke toggles instead.
    const addMembers = async (players: PermissionPlayer[]) => {
        if (!selected || selected.type !== "group") {
            return;
        }
        const group = selected.group.name;
        const added: PermissionPlayer[] = [];
        for (const p of players) {
            const res = await rpc_setPlayerGroupMembership(socket, p.steamId, group, true);
            if (res.success) {
                added.push(p);
            }
        }
        if (added.length > 0) {
            setGroupMembers(prev => [...(prev ?? []), ...added]);
        }
    };

    const leftQuery = leftSearch.trim().toLowerCase();
    const filteredGroups = leftQuery
        ? groups.filter(g => g.name.toLowerCase().includes(leftQuery) || (g.title ?? "").toLowerCase().includes(leftQuery))
        : groups;

    const onPlayerListScroll = () => {
        const el = playerListRef.current;
        if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 100) {
            playerDirectory.loadMore();
        }
    };

    const grantedSet = useMemo(() => {
        if (!selected) {
            return new Set<string>();
        }
        if (selected.type === "player") {
            return new Set(playerPerms?.direct ?? []);
        }
        return new Set(groupPerms ?? []);
    }, [selected, playerPerms, groupPerms]);

    const inheritedMap = useMemo(() => {
        if (selected?.type !== "player" || !playerPerms) {
            return undefined;
        }
        const map = new Map<string, string[]>();
        for (const inh of playerPerms.inherited) {
            const arr = map.get(inh.permission) ?? [];
            arr.push(inh.group);
            map.set(inh.permission, arr);
        }
        return map;
    }, [selected, playerPerms]);

    const selectionKey = selected
        ? (selected.type === "player" ? "player:" + selected.player.steamId : "group:" + selected.group.name)
        : "none";

    if (!socket.isConnected || !serverInfo.backendConnected) {
        return <CenteredMessage>Connecting…</CenteredMessage>;
    }

    if (!serverInfo.serverConnected) {
        return (
            <div className="size-full flex flex-col items-center justify-center p-4 text-center space-y-2">
                <LucideCircleSlash className="text-red-500" size={40}/>
                <p className="text-red-500 text-xl">Server not connected</p>
                <p className="text-neutral-400 max-w-md">
                    Permissions can only be viewed and edited while the server is connected.
                </p>
            </div>
        );
    }

    if (!loaded) {
        return <CenteredMessage>Loading permissions…</CenteredMessage>;
    }

    // The backend returns null (not an empty list) when the permission commands fail, which happens when
    // the RustServerTool plugin isn't installed (the server never replies).
    if (!available) {
        return (
            <div className="size-full flex flex-col items-center justify-center p-4 text-center space-y-2">
                <LucideCircleSlash className="text-red-500" size={40}/>
                <p className="text-red-500 text-xl">Permissions manager unavailable</p>
                <p className="text-neutral-400 max-w-md">
                    Managing permissions requires the <a className="text-primary hover:underline" rel="noreferrer"
                    href="https://codefling.com/plugins/rust-server-tool" target="_blank">RustServerTool plugin</a> to
                    be installed on your server.
                </p>
            </div>
        );
    }

    return (
        <div className="size-full flex flex-col p-4">
            {showAddPlayers && selected?.type === "group" && (
                <AddPlayersOverlay
                    existingMemberIds={new Set((groupMembers ?? []).map(m => m.steamId))}
                    onAdd={addMembers}
                    hide={() => setShowAddPlayers(false)}
                />
            )}

            <h1 className="text-2xl font-bold text-white mb-4">Permissions Manager</h1>

            <div className="flex-1 flex flex-row gap-4 min-h-0">
                {/* LEFT COLUMN — selection */}
                <div className="w-96 flex-shrink-0 flex flex-col rounded-md bg-neutral-800 border border-neutral-700 overflow-hidden">
                    <div className="flex border-b border-neutral-700 px-2">
                        <TabButton active={leftTab === "player"} onClick={() => setLeftTab("player")}>Players</TabButton>
                        <TabButton active={leftTab === "group"} onClick={() => setLeftTab("group")}>Groups</TabButton>
                    </div>
                    <div className="p-2">
                        <SearchInput value={leftSearch} onChange={setLeftSearch}
                                     placeholder={leftTab === "player" ? "Search players..." : "Search groups..."}/>
                    </div>
                    <div ref={playerListRef} onScroll={leftTab === "player" ? onPlayerListScroll : undefined}
                         className="flex-1 overflow-y-auto rst-scrollbar px-2 pb-2 space-y-1">
                        {leftTab === "player" && playerDirectory.players.map(p => {
                            const isSel = selected?.type === "player" && selected.player.steamId === p.steamId;
                            return (
                                <button key={p.steamId} onClick={() => selectPlayer(p)}
                                        className={`w-full text-left px-3 py-2 rounded-md transition-colors ${isSel ? "bg-neutral-700" : "hover:bg-neutral-700/50"}`}>
                                    <div className="font-medium text-white truncate">{p.displayName}</div>
                                    <div className="text-xs text-neutral-400 font-mono">{p.steamId}</div>
                                </button>
                            );
                        })}
                        {leftTab === "player" && playerDirectory.isLoading && (
                            <p className="text-neutral-500 text-sm text-center py-3">Loading players…</p>
                        )}
                        {leftTab === "player" && !playerDirectory.isLoading && playerDirectory.players.length === 0 && (
                            <p className="text-neutral-500 text-sm text-center py-6">No players found.</p>
                        )}
                        {leftTab === "group" && filteredGroups.map(g => {
                            const isSel = selected?.type === "group" && selected.group.name === g.name;
                            return (
                                <button key={g.name} onClick={() => selectGroup(g)}
                                        className={`w-full text-left px-3 py-2 rounded-md transition-colors ${isSel ? "bg-neutral-700" : "hover:bg-neutral-700/50"}`}>
                                    <div className="font-medium text-white truncate">{g.title || g.name}</div>
                                    {g.title && <div className="text-xs text-neutral-400 truncate">{g.name}</div>}
                                </button>
                            );
                        })}
                        {leftTab === "group" && filteredGroups.length === 0 && (
                            <p className="text-neutral-500 text-sm text-center py-6">No groups found.</p>
                        )}
                    </div>
                </div>

                {/* RIGHT COLUMN — editor */}
                <div className="flex-1 min-w-0 flex flex-col rounded-md bg-neutral-900 border border-neutral-700 overflow-hidden">
                    {!selected ? (
                        <div className="flex-1 flex items-center justify-center text-neutral-500 px-4 text-center">
                            Select a player or group to edit its permissions.
                        </div>
                    ) : (
                        <>
                            <div className="border-b border-neutral-700 px-4 pt-3">
                                {selected.type === "player" ? (
                                    <div className="mb-2">
                                        <div className="flex items-center gap-1.5">
                                            <h2 onClick={() => overlay.openPlayerOverlay(selected.player.steamId)}
                                                className="text-lg font-bold text-white truncate min-w-0 hover:underline cursor-pointer">
                                                {selected.player.displayName}
                                            </h2>
                                            <button onClick={() => overlay.openPlayerOverlay(selected.player.steamId)}
                                                    title="Open player details"
                                                    className="flex-shrink-0 text-neutral-400 hover:text-white">
                                                <LucideExternalLink size={16}/>
                                            </button>
                                        </div>
                                        <p className="text-xs text-neutral-400 font-mono">{selected.player.steamId}</p>
                                    </div>
                                ) : (
                                    <div className="mb-2">
                                        <h2 className="text-lg font-bold text-white truncate">{selected.group.title || selected.group.name}</h2>
                                        <p className="text-xs text-neutral-400">
                                            {selected.group.title ? `${selected.group.name} · ` : ""}{groupMembers?.length ?? 0} members
                                        </p>
                                    </div>
                                )}
                                <div className="flex">
                                    <TabButton active={rightTab === "permissions"} onClick={() => setRightTab("permissions")}>
                                        Permissions
                                    </TabButton>
                                    <TabButton active={rightTab === "membership"} onClick={() => setRightTab("membership")}>
                                        {selected.type === "player" ? "Groups" : "Players"}
                                    </TabButton>
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto rst-scrollbar p-4">
                                {rightTab === "permissions" && (
                                    (selected.type === "player" ? playerPerms : groupPerms) === undefined ? (
                                        <p className="text-neutral-500 text-sm text-center py-6">Loading…</p>
                                    ) : (
                                        <PermissionList
                                            key={selectionKey}
                                            allPermissions={allPermissions}
                                            granted={grantedSet}
                                            inherited={inheritedMap}
                                            onToggle={togglePermission}
                                        />
                                    )
                                )}

                                {rightTab === "membership" && selected.type === "player" && (
                                    playerPerms === undefined ? (
                                        <p className="text-neutral-500 text-sm text-center py-6">Loading…</p>
                                    ) : (
                                        <GroupToggleList
                                            groups={groups}
                                            memberGroups={new Set(playerPerms.groups)}
                                            onToggle={togglePlayerGroup}
                                        />
                                    )
                                )}

                                {rightTab === "membership" && selected.type === "group" && (
                                    groupMembers === undefined ? (
                                        <p className="text-neutral-500 text-sm text-center py-6">Loading…</p>
                                    ) : (
                                        <GroupMembersPanel
                                            members={groupMembers}
                                            onRemove={removeMember}
                                            onAddClick={() => setShowAddPlayers(true)}
                                        />
                                    )
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function CenteredMessage(props: { children: React.ReactNode }) {
    return (
        <div className="size-full flex items-center justify-center text-neutral-400 p-4 text-center">
            {props.children}
        </div>
    );
}

function describeError(message: string): string {
    switch (message) {
        case "not_connected":
            return "The server is not connected.";
        case "no_permission":
            return "You do not have permission to do this.";
        case "invalid_argument":
            return "Invalid request.";
        default:
            return message || "Action failed.";
    }
}
